import { c as _c } from "react/compiler-runtime";
import { skipToken, useQuery, useTRPC } from '@/api/trpc';
import { useMemo } from 'react';
import { GetIntegrationListParams } from '@repo/common/schemas/api';
import type { AccountStatus } from '@repo/common/schemas/models';
import * as R from 'remeda';
export function useAccount(accountId) {
  const $ = _c(7);
  const trpc = useTRPC();
  let t0;
  if ($[0] !== accountId || $[1] !== trpc.accounts.byId) {
    t0 = trpc.accounts.byId.queryOptions(accountId ? {
      accountId
    } : skipToken);
    $[0] = accountId;
    $[1] = trpc.accounts.byId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    data: account,
    isLoading,
    error
  } = useQuery(t0);
  let t1;
  if ($[3] !== account || $[4] !== error || $[5] !== isLoading) {
    t1 = {
      account,
      isLoading,
      error
    };
    $[3] = account;
    $[4] = error;
    $[5] = isLoading;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

// TODO: this is mostly redundant with useAllAccounts()
export function useAccounts(filters) {
  const $ = _c(9);
  const trpc = useTRPC();
  let t0;
  if ($[0] !== filters || $[1] !== trpc.accounts.list) {
    let t1;
    if ($[3] !== filters) {
      t1 = filters ?? {};
      $[3] = filters;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = trpc.accounts.list.queryOptions(t1);
    $[0] = filters;
    $[1] = trpc.accounts.list;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    data: accounts,
    error,
    isLoading
  } = useQuery(t0);
  let t1;
  if ($[5] !== accounts || $[6] !== error || $[7] !== isLoading) {
    t1 = {
      accounts,
      error,
      isLoading
    };
    $[5] = accounts;
    $[6] = error;
    $[7] = isLoading;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}

/**
 * Hook to fetch integrations for an account using React Query
 */
export function useIntegrations(accountId, params, options) {
  const $ = _c(13);
  const trpc = useTRPC();
  let t0;
  if ($[0] !== accountId || $[1] !== options || $[2] !== params || $[3] !== trpc.integrations.list) {
    t0 = trpc.integrations.list.queryOptions({
      accountId,
      params
    }, options);
    $[0] = accountId;
    $[1] = options;
    $[2] = params;
    $[3] = trpc.integrations.list;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const {
    data: integrations,
    isLoading,
    isFetching,
    error,
    dataUpdatedAt
  } = useQuery(t0);
  let t1;
  if ($[5] !== integrations) {
    t1 = integrations ?? [];
    $[5] = integrations;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== dataUpdatedAt || $[8] !== error || $[9] !== isFetching || $[10] !== isLoading || $[11] !== t1) {
    t2 = {
      integrations: t1,
      isLoading,
      isFetching,
      error,
      dataUpdatedAt
    };
    $[7] = dataUpdatedAt;
    $[8] = error;
    $[9] = isFetching;
    $[10] = isLoading;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
export function usePrimaryIntegration(accountId) {
  const $ = _c(7);
  const trpc = useTRPC();
  const {
    account,
    isLoading: isAccountLoading,
    error: accountError
  } = useAccount(accountId);
  const id = account?.primaryIntegration?._id;
  let t0;
  if ($[0] !== id || $[1] !== trpc.integrations.byId) {
    t0 = trpc.integrations.byId.queryOptions(id ? {
      id
    } : skipToken);
    $[0] = id;
    $[1] = trpc.integrations.byId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    data: integration,
    isLoading: isIntegrationLoading,
    error: integrationError
  } = useQuery(t0);
  const t1 = isIntegrationLoading || isAccountLoading;
  const t2 = integrationError || accountError;
  let t3;
  if ($[3] !== integration || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      integration,
      isLoading: t1,
      error: t2
    };
    $[3] = integration;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// Hook to get an account + integration by corpId, filtered by the authenticated user's permissions
// If the user can only access 1 integration and didn't provide a corpId, use that one
export function useAccountByCorpId(corpId) {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "bullhorn",
      status: "active"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    integrations: t1,
    isLoading,
    error
  } = useIntegrations(null, t0);
  let t2;
  if ($[1] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const allIntegrations = t2;
  let t3;
  if ($[3] !== allIntegrations || $[4] !== corpId) {
    t3 = corpId ? allIntegrations.find(integration => integration.corpId === corpId) : R.only(allIntegrations);
    $[3] = allIntegrations;
    $[4] = corpId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const integration_0 = t3;
  const account = integration_0?.account;
  let t4;
  if ($[6] !== account || $[7] !== error || $[8] !== integration_0 || $[9] !== isLoading) {
    t4 = {
      account,
      integration: integration_0,
      isLoading,
      error
    };
    $[6] = account;
    $[7] = error;
    $[8] = integration_0;
    $[9] = isLoading;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}